<template>
	<ValidationForm #default="{ handleSubmit }">
		<Modal max-width="800px">
			<template #modal-title>
				<IconButton
					v-if="quickScanMode"
					data-testid="back-button"
					@click="exitQuickScanMode"
				>
					mdi-arrow-left
				</IconButton>
				<span class="title">
					{{ quickScanMode ? 'Quick Scan' : 'Start a New Scan' }}
				</span>
				<v-spacer />
				<IconButton
					data-testid="close-button"
					@click="$emit('close')"
				>
					mdi-close
				</IconButton>
			</template>
			<template #modal-content>
				<!-- Initial choice view -->
				<div v-if="!quickScanMode">
					<p class="subtitle-1 text--secondary mb-6 text-center">
						Choose the type of scan you'd like to run
					</p>
					<v-row class="justify-center align-stretch">
						<v-col
							cols="12"
							sm="6"
							class="d-flex"
						>
							<OverviewCard
								class="scan-option"
								use-grey
								icon="mdi-web"
								title="Full Scan"
								description="Comprehensive cookie & tracker scan. Recommended for most use cases."
								data-testid="full-scan-option"
								@overview="onFullScan"
							/>
						</v-col>
						<v-col
							cols="12"
							sm="6"
							class="d-flex"
						>
							<OverviewCard
								class="scan-option"
								use-grey
								icon="mdi-flash"
								title="Quick Scan"
								description="Run a lightweight spot-check scan with minimal configuration."
								data-testid="quick-scan-option"
								@overview="enterQuickScanMode"
							/>
						</v-col>
					</v-row>
				</div>
				<!-- Quick scan form view -->
				<div
					v-else
					class="quick-scan-form"
				>
					<v-row>
						<v-col
							cols="12"
							sm="12"
						>
							<h3 class="question-title mb-2">
								Which URL would you like to scan?
							</h3>
							<TextField
								v-model="quickScan.url"
								label="URL"
								rules="required|isUrlwithHttp"
								data-testid="quick-scan-url"
							/>
						</v-col>
						<v-col
							v-if="clientMultiBranded || !singleBrand"
							cols="12"
							sm="12"
						>
							<h3 class="question-title mb-2">
								Which Brand would you like to associate with this scan?
							</h3>
							<Dropdown
								v-model="quickScan.brandId"
								label="Brand"
								:items="brandOptions"
								clearable
								custom-sort
								searchable
								rules="required"
								data-testid="quick-scan-brand"
							/>
						</v-col>
						<v-col
							cols="12"
							sm="12"
						>
							<h3 class="question-title mb-2">
								What email address should we send scan updates to?
							</h3>
							<TextField
								v-model="quickScan.email"
								label="Notification Email"
								rules="required|email"
								data-testid="quick-scan-email"
							/>
						</v-col>
					</v-row>
				</div>
			</template>
			<template
				v-if="quickScanMode"
				#modal-footer
			>
				<SecondaryActionButton
					data-testid="quick-scan-cancel"
					class="mr-2"
					@click="$emit('close')"
				>
					Cancel
				</SecondaryActionButton>
				<v-spacer />
				<PrimaryActionButton
					data-testid="quick-scan-submit"
					@click="handleSubmit(submitQuickScan)"
				>
					Request Scan
				</PrimaryActionButton>
			</template>
		</Modal>
	</ValidationForm>
</template>

<script>
import ValidationForm from '../../../../../../shared/components/validation-form.vue'
import Modal from '../../../../../../shared/components/modal.vue'
import OverviewCard from '../../../../../../shared/components/overview-card.vue'
import IconButton from '../../../../../../shared/components/icon-button.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import { getServerTimezoneInfo } from '../../../../../../shared/utils/api/server-timezone.js'
import { requestScheduledScan } from '../../../../../../shared/utils/api/site-scanner.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
export default {
	name: 'site-scanner-scan-type-modal',
	components: { ValidationForm, Modal, OverviewCard, IconButton, TextField, Dropdown, PrimaryActionButton, SecondaryActionButton },
	props: {
		brandOptions: { type: Array, default: () => [] },
		clientMultiBranded: { type: Boolean, default: true },
		singleBrand: { type: Boolean, default: false },
		defaultBrandInSelect: { type: [String, Number], default: null },
		userAgents: { type: Array, default: () => [] }
	},
	setup () {
		return {
			showSnackbar
		}
	},
	data () {
		return {
			quickScanMode: false,
			quickScan: {
				url: '',
				brandId: this.defaultBrandInSelect,
				email: ''
			},
			serverTimeInfo: {
				currentServerTime: null,
				serverTimezone: null,
				utcOffsent: null
			}
		}
	},
	async created () {
		const timezoneResponse = await getServerTimezoneInfo()
		this.serverTimeInfo = timezoneResponse.data
	},
	methods: {
		onFullScan () {
			this.$emit('full-scan')
		},
		enterQuickScanMode () {
			this.quickScanMode = true
		},
		exitQuickScanMode () {
			this.quickScanMode = false
		},
		customToISOString (date) {
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			const hours = String(date.getHours()).padStart(2, '0')
			const minutes = String(date.getMinutes()).padStart(2, '0')
			const seconds = String(date.getSeconds()).padStart(2, '0')
			return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
		},
		async requestNowScanDate () {
			const timezoneResponse = await getServerTimezoneInfo()
			this.serverTimeInfo = timezoneResponse.data
			const newRequestNowDate = new Date(this.serverTimeInfo?.currentServerTime)
			newRequestNowDate.setSeconds(newRequestNowDate.getSeconds() + 30)
			return newRequestNowDate
		},
		async submitQuickScan () {
			// eslint-disable-next-line no-console
			const defaultUserAgent = this.userAgents.find(agent => agent.isDefault)
			const quickScanForm = new FormData()
			quickScanForm.append('ScanName', this.quickScan.url)
			quickScanForm.append('BrandId', this.quickScan.brandId)
			quickScanForm.append('EmailAddressToNotify', this.quickScan.email)
			quickScanForm.append('ScheduledScanDateAndTime', this.customToISOString(await this.requestNowScanDate()))
			quickScanForm.append('DeepCrawl', false)
			quickScanForm.append('Domain', this.quickScan.url)
			quickScanForm.append('UseRobots', false)
			quickScanForm.append('ScanType', 0)
			quickScanForm.append('ScanDelayPeriod', 0)
			quickScanForm.append('PageTimeout', 5000)
			quickScanForm.append('Frequency', 1)
			quickScanForm.append('GpcEnabled', false)
			quickScanForm.append('Headless', false)
			quickScanForm.append('UserAgent', defaultUserAgent.id)
			try {
				await requestScheduledScan(quickScanForm)
				this.$emit('handle-quick-scan-requested')
				this.quickScanMode = false
			} catch (error) {
				showSnackbar({ text: `Failed to request quick scan: ${error.message}`, color: 'red' })
			}
		}
	}
}
</script>

<style scoped>
.scan-option {
	width: 100%;
	display: flex;
	flex-direction: column;
}
.scan-option >>> .overview-card {
	height: 100%;
	display: flex;
	flex-direction: column;
}
.scan-option >>> .overview-card .v-card__text {
	flex-grow: 1;
}
.quick-scan-form {
	margin-top: 8px;
}

.question-title {
	margin: 0;
	font-weight: 500;
	font-size: 14px;
	line-height: 1.4;
}
</style>
